import * as React from "react";

import { TimeDisplayMode } from "@/shared/components/visualization/WorkspaceCtx/TimeDisplayMode";
import {
  DEFAULT_FRACTION_DIGITS,
  FractionDigits,
  isFractionDigits,
} from "@/shared/time";

import {
  TimeAnchor,
  TimeAnchorsByThread,
  decodeAnchors,
  encodeAnchors,
  withAnchor,
} from "./timeAnchor";
import {
  DEFAULT_TIMESTAMP_FORMAT,
  TIMESTAMP_FORMATS,
  TimestampFormat,
  TimestampRenderOptions,
  VisualizerClock,
  isAbsoluteTimestampFormat,
  isTimestampFormat,
  resolveDisplayFormat,
} from "./timestampFormat";

/**
 * Names the t=0 the outgoing context carries while the thread is following the
 * visualizer's elapsed display — the same instant that display counts from.
 * Written for the agent to quote back at the reader, so it says both what the
 * instant is and why the thread is using it.
 */
export const FOLLOWED_VISUALIZER_ANCHOR_LABEL =
  "Start of the visualizer's loaded data (following the visualizer)";

interface TimestampFormatContextValue {
  /** What the user selected. May be `Relative` even with no anchor yet. */
  format: TimestampFormat;
  /**
   * What to actually render with: `format`, except that an anchorless
   * `Relative` and a visualizer-less `FollowVisualizer` fall back to the last
   * absolute format the user chose.
   */
  displayFormat: TimestampFormat;
  setFormat: (format: TimestampFormat) => void;
  /**
   * The formats the reader may choose from right now, in display order. Every
   * format except `FollowVisualizer`, which appears only while a visualizer is
   * open — with none there is nothing to follow, and a mode that renders as
   * something else entirely is worse than one that isn't offered.
   */
  availableFormats: readonly TimestampFormat[];
  precision: FractionDigits;
  setPrecision: (precision: FractionDigits) => void;
  /** The thread's t=0 for `Relative`. Undefined until the reader picks one. */
  anchor: TimeAnchor | undefined;
  /** Sets the thread's t=0; `undefined` clears it. */
  setAnchor: (anchor: TimeAnchor | undefined) => void;
  /**
   * The open visualizer's time display, or undefined when none is open — the
   * same value `renderOptions` carries, exposed so the settings UI can preview
   * `FollowVisualizer` as it will actually read.
   */
  visualizerClock: VisualizerClock | undefined;
  /** Ready to hand to `formatTimestamp`. */
  renderOptions: TimestampRenderOptions;
}

const TimestampFormatContext = React.createContext<
  TimestampFormatContextValue | undefined
>(undefined);

/**
 * The half of the provider that never changes: the setters, plus a read of the
 * anchor timestamps are *currently* rendered against.
 *
 * Split out because the value context above changes identity on every format,
 * precision or anchor change, and its consumers re-render with it. A component
 * that only acts on the format — the transcript root, which attaches the
 * reader's t=0 to an outgoing message — would otherwise re-render the whole
 * conversation (re-parsing every message's markdown) each time the reader
 * nudged a decimal place.
 */
interface TimestampFormatActions {
  setFormat: (format: TimestampFormat) => void;
  setPrecision: (precision: FractionDigits) => void;
  setAnchor: (anchor: TimeAnchor | undefined) => void;
  /**
   * The t=0 timestamps are being rendered against right now — its instant and
   * the name the reader sees for it — or undefined when they carry no origin
   * at all. A function rather than a value so that reading it costs no
   * re-render: callers read it at the moment they act, which while the thread
   * follows the visualizer is also the only moment its value is settled.
   *
   * Two display formats have an origin. `Relative` counts from the thread's
   * own anchor. `FollowVisualizer` counts from the visualizer's earliest
   * loaded instant, but only in the two elapsed modes — its absolute modes
   * (Log, Local, UTC) render wall-clock instants, which have no t=0 to name.
   */
  getDisplayAnchor: () => { ns: bigint; label: string } | undefined;
}

const TimestampFormatActionsContext = React.createContext<
  TimestampFormatActions | undefined
>(undefined);

interface TimestampFormatProviderProps {
  children: React.ReactNode;
  /**
   * localStorage key used to persist the selected format. Three further keys
   * are derived from it (see {@link derivedKeys}). When unset, the provider is
   * ephemeral (state held in memory only). Callers mounting the provider in
   * distinct parts of the app should supply distinct keys so the preferences
   * don't collide.
   */
  storageKey?: string;
  /** Format to use when no persisted value is found. */
  defaultFormat?: TimestampFormat;
  /**
   * Thread whose anchor is in play; anchors are per thread. Without it a picked
   * anchor is held in memory and committed once an id arrives, which is what
   * the sidebar needs while a brand-new thread has not been created yet.
   */
  threadId?: string;
  /**
   * The open visualizer's time display, re-supplied by the caller as it
   * changes, which is what lets `FollowVisualizer` inherit a mode switch or a
   * newly loaded file as it happens. Nothing is persisted from it: following is
   * a live read, so closing the visualizer takes the mode off the menu again
   * and the format falls back until one reopens.
   */
  visualizerClock?: VisualizerClock;
}

function derivedKeys(storageKey: string | undefined) {
  if (storageKey === undefined) {
    return undefined;
  }
  return {
    format: storageKey,
    // The format to fall back to while Relative has no anchor. Kept separate
    // from `format` so selecting Relative doesn't destroy the user's previous
    // absolute choice.
    absoluteFormat: `${storageKey}-absolute`,
    precision: `${storageKey}-precision`,
    anchors: `${storageKey}-anchors`,
  };
}

function readStored(key: string | undefined): string | null {
  if (key === undefined) {
    return null;
  }
  try {
    return localStorage.getItem(key);
  } catch {
    // Storage unavailable (Safari private mode, disabled cookies, etc.)
    return null;
  }
}

function writeStored(key: string | undefined, value: string): void {
  if (key === undefined) {
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch {
    // Ignore quota / disabled-storage errors
  }
}

function parseFormat(
  stored: string | null,
  fallback: TimestampFormat,
): TimestampFormat {
  return stored !== null && isTimestampFormat(stored) ? stored : fallback;
}

/**
 * Neither context-dependent format can serve as the fallback the other
 * resolves to, so a stored or default `Relative` or `FollowVisualizer` yields
 * the built-in absolute default instead.
 */
function parseAbsoluteFormat(
  stored: string | null,
  fallback: TimestampFormat,
): TimestampFormat {
  const parsed = parseFormat(stored, fallback);
  return isAbsoluteTimestampFormat(parsed) ? parsed : DEFAULT_TIMESTAMP_FORMAT;
}

/**
 * The t=0 a thread following the visualizer is counting from, or undefined
 * when it isn't counting from one.
 *
 * Only the visualizer's two elapsed modes have an origin; its Log, Local and
 * UTC modes render absolute wall-clock instants, and naming a t=0 alongside
 * those would invite the agent to read the reader's absolute times as offsets.
 */
function followedVisualizerAnchor(
  clock: VisualizerClock | undefined,
): { ns: bigint; label: string } | undefined {
  if (clock === undefined || clock.earliestNs === undefined) {
    return undefined;
  }
  if (
    clock.mode !== TimeDisplayMode.Elapsed &&
    clock.mode !== TimeDisplayMode.ElapsedTime
  ) {
    return undefined;
  }
  return { ns: clock.earliestNs, label: FOLLOWED_VISUALIZER_ANCHOR_LABEL };
}

/**
 * Digit count from its stored decimal string. Absent and blank values are
 * rejected before `Number`, which reads both as 0 — itself a valid count.
 */
function parsePrecision(stored: string | null): FractionDigits {
  if (stored === null || stored.trim() === "") {
    return DEFAULT_FRACTION_DIGITS;
  }
  const parsed = Number(stored);
  return isFractionDigits(parsed) ? parsed : DEFAULT_FRACTION_DIGITS;
}

export const TimestampFormatProvider: React.FC<
  TimestampFormatProviderProps
> = ({
  children,
  storageKey,
  defaultFormat = DEFAULT_TIMESTAMP_FORMAT,
  threadId,
  visualizerClock,
}) => {
  const keys = React.useMemo(() => derivedKeys(storageKey), [storageKey]);

  const [format, setFormatState] = React.useState<TimestampFormat>(() =>
    parseFormat(readStored(keys?.format), defaultFormat),
  );
  const [absoluteFormat, setAbsoluteFormatState] =
    React.useState<TimestampFormat>(() =>
      parseAbsoluteFormat(readStored(keys?.absoluteFormat), defaultFormat),
    );
  const [precision, setPrecisionState] = React.useState<FractionDigits>(() =>
    parsePrecision(readStored(keys?.precision)),
  );
  const [anchors, setAnchorsState] = React.useState<TimeAnchorsByThread>(() =>
    decodeAnchors(readStored(keys?.anchors)),
  );
  // Holds an anchor pinned before the thread exists; committed by the effect
  // below once an id arrives.
  const [pendingAnchor, setPendingAnchor] = React.useState<
    TimeAnchor | undefined
  >(undefined);

  const setFormat = React.useCallback(
    (next: TimestampFormat) => {
      // Callers that bypass the type system (dynamic JSON, relaxed casts) must
      // not get an unrecognised value into storage, where the next load would
      // silently swap it for the default.
      if (!isTimestampFormat(next)) {
        return;
      }
      setFormatState(next);
      writeStored(keys?.format, next);
      if (isAbsoluteTimestampFormat(next)) {
        setAbsoluteFormatState(next);
        writeStored(keys?.absoluteFormat, next);
      }
    },
    [keys],
  );

  const setPrecision = React.useCallback(
    (next: FractionDigits) => {
      if (!isFractionDigits(next)) {
        return;
      }
      setPrecisionState(next);
      writeStored(keys?.precision, String(next));
    },
    [keys],
  );

  /** Writes one thread's pinned anchor to state and storage together. */
  const commitAnchor = React.useCallback(
    (id: string, next: TimeAnchor | undefined) => {
      setAnchorsState((current) => {
        const updated = withAnchor(current, id, next);
        writeStored(keys?.anchors, encodeAnchors(updated));
        return updated;
      });
    },
    [keys],
  );

  const setAnchor = React.useCallback(
    (next: TimeAnchor | undefined) => {
      if (threadId === undefined) {
        setPendingAnchor(next);
        return;
      }
      commitAnchor(threadId, next);
    },
    [commitAnchor, threadId],
  );

  React.useEffect(() => {
    if (threadId === undefined || pendingAnchor === undefined) {
      return;
    }
    commitAnchor(threadId, pendingAnchor);
    setPendingAnchor(undefined);
  }, [commitAnchor, threadId, pendingAnchor]);

  // Cross-tab sync: when another tab writes one of our keys, recompute what a
  // fresh mount here would read, so the user's preferences stay consistent
  // across windows — a cleared key returns to the default and an unrecognised
  // one counts as no preference at all. The 'storage' event only fires in
  // *other* tabs, so there's no echo loop.
  React.useEffect(() => {
    if (keys === undefined) {
      return;
    }
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea !== localStorage) {
        return;
      }
      switch (event.key) {
        case keys.format:
          setFormatState(parseFormat(event.newValue, defaultFormat));
          return;
        case keys.absoluteFormat:
          setAbsoluteFormatState(
            parseAbsoluteFormat(event.newValue, defaultFormat),
          );
          return;
        case keys.precision:
          setPrecisionState(parsePrecision(event.newValue));
          return;
        case keys.anchors:
          setAnchorsState(decodeAnchors(event.newValue));
          return;
        default:
          return;
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [keys, defaultFormat]);

  // A pending anchor stays on screen until the effect above commits it, so the
  // display doesn't flash back to absolute for a frame when the id arrives.
  const anchor =
    (threadId === undefined ? undefined : anchors[threadId]) ?? pendingAnchor;
  const displayFormat = resolveDisplayFormat(
    format,
    anchor?.ns,
    absoluteFormat,
    visualizerClock,
  );

  // Refreshed on every render so `getDisplayAnchor` can stay identity-stable
  // and still answer for the current state.
  const displayRef = React.useRef({ displayFormat, anchor, visualizerClock });
  displayRef.current = { displayFormat, anchor, visualizerClock };

  const getDisplayAnchor = React.useCallback(() => {
    const current = displayRef.current;
    if (current.displayFormat === TimestampFormat.Relative) {
      return current.anchor === undefined
        ? undefined
        : { ns: current.anchor.ns, label: current.anchor.label };
    }
    if (current.displayFormat === TimestampFormat.FollowVisualizer) {
      return followedVisualizerAnchor(current.visualizerClock);
    }
    return undefined;
  }, []);

  const actions = React.useMemo(
    () => ({ setFormat, setPrecision, setAnchor, getDisplayAnchor }),
    [setFormat, setPrecision, setAnchor, getDisplayAnchor],
  );

  const availableFormats = React.useMemo(
    () =>
      visualizerClock === undefined
        ? TIMESTAMP_FORMATS.filter(
            (candidate) => candidate !== TimestampFormat.FollowVisualizer,
          )
        : TIMESTAMP_FORMATS,
    [visualizerClock],
  );

  const value = React.useMemo(
    () => ({
      format,
      displayFormat,
      setFormat,
      availableFormats,
      precision,
      setPrecision,
      anchor,
      setAnchor,
      visualizerClock,
      renderOptions: {
        fractionDigits: precision,
        anchorNs: anchor?.ns,
        visualizerClock,
      },
    }),
    [
      format,
      displayFormat,
      availableFormats,
      anchor,
      visualizerClock,
      precision,
      setFormat,
      setPrecision,
      setAnchor,
    ],
  );

  return (
    <TimestampFormatActionsContext.Provider value={actions}>
      <TimestampFormatContext.Provider value={value}>
        {children}
      </TimestampFormatContext.Provider>
    </TimestampFormatActionsContext.Provider>
  );
};

/**
 * Returns the active format, precision, anchor and their setters. Returns
 * `undefined` when called outside a {@link TimestampFormatProvider}; consumers
 * should treat that as "use the defaults" rather than throwing, because
 * Timestamp is rendered in contexts outside the AgentThread sidebar as well.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTimestampFormat(): TimestampFormatContextValue | undefined {
  return React.useContext(TimestampFormatContext);
}

/**
 * Returns the setters and the live display-anchor read, as an object whose
 * identity never changes. Prefer it over {@link useTimestampFormat} wherever a
 * component only *changes* the format rather than rendering with it: the value
 * context re-renders its consumers on every change, this one never does.
 *
 * `undefined` outside a {@link TimestampFormatProvider}, on the same terms.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTimestampFormatActions():
  | TimestampFormatActions
  | undefined {
  return React.useContext(TimestampFormatActionsContext);
}
